import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiChevronDown, FiChevronRight, FiTool, FiMinusCircle, FiDownload } from 'react-icons/fi';
import activosFijosService from '../services/activosFijosService';
import Loading from '../components/Loading';
import './ActivosFijosPage.css';

const ensureArray = (v) => Array.isArray(v) ? v : [];

const ActivosFijosPage = () => {
  const [activos, setActivos] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [bajaModalOpen, setBajaModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroMetodo, setFiltroMetodo] = useState('');
  const [activoBaja, setActivoBaja] = useState(null);
  const [motivoBaja, setMotivoBaja] = useState('');
  const [fechaBaja, setFechaBaja] = useState('');

  const [form, setForm] = useState({
    nombre: '', codigo_activo: '', categoria: '', fecha_adquisicion: '', valor_adquisicion: '',
    valor_residual: 0, vida_util_meses: 60, metodo_depreciacion: 'lineal',
    plan_cuenta_activo: '', plan_cuenta_depreciacion: '', centro_costo: '',
  });

  useEffect(() => { cargarDatos(); }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filtroEstado) filters.estado = filtroEstado;
      if (filtroMetodo) filters.metodo_depreciacion = filtroMetodo;
      const [activosRes, resumenRes] = await Promise.all([
        activosFijosService.getAll(filters),
        activosFijosService.resumenPorCategoria().catch(() => ({ data: [] })),
      ]);
      setActivos(ensureArray(activosRes.data.results || activosRes.data));
      setResumen(ensureArray(resumenRes.data.results || resumenRes.data));
    } catch { toast.error('Error al cargar activos fijos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargarDatos(); }, [filtroEstado, filtroMetodo]);

  const handleSave = async () => {
    try {
      const data = { ...form };
      if (!data.centro_costo) delete data.centro_costo;
      if (!data.plan_cuenta_activo) delete data.plan_cuenta_activo;
      if (!data.plan_cuenta_depreciacion) delete data.plan_cuenta_depreciacion;
      if (editing) {
        await activosFijosService.update(editing.id, data);
        toast.success('Activo actualizado');
      } else {
        await activosFijosService.create(data);
        toast.success('Activo creado');
      }
      setModalOpen(false); setEditing(null); cargarDatos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este activo?')) return;
    try { await activosFijosService.delete(id); toast.success('Eliminado'); cargarDatos(); }
    catch { toast.error('Error al eliminar'); }
  };

  const handleDepreciar = async (id) => {
    if (!window.confirm('¿Generar depreciacion mensual para este activo?')) return;
    try { await activosFijosService.depreciar(id); toast.success('Depreciacion generada'); cargarDatos(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error al depreciar'); }
  };

  const handleDarDeBaja = async () => {
    if (!activoBaja) return;
    try {
      await activosFijosService.darDeBaja(activoBaja.id, { motivo: motivoBaja, fecha_baja: fechaBaja });
      toast.success('Activo dado de baja');
      setBajaModalOpen(false); setActivoBaja(null); cargarDatos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al dar de baja'); }
  };

  const handleEdit = (a) => {
    setEditing(a);
    setForm({ nombre: a.nombre, codigo_activo: a.codigo_activo, categoria: a.categoria || '', fecha_adquisicion: a.fecha_adquisicion, valor_adquisicion: a.valor_adquisicion, valor_residual: a.valor_residual, vida_util_meses: a.vida_util_meses, metodo_depreciacion: a.metodo_depreciacion, plan_cuenta_activo: a.plan_cuenta_activo || '', plan_cuenta_depreciacion: a.plan_cuenta_depreciacion || '', centro_costo: a.centro_costo || '' });
    setModalOpen(true);
  };

  const resetForm = () => setForm({ nombre: '', codigo_activo: '', categoria: '', fecha_adquisicion: '', valor_adquisicion: '', valor_residual: 0, vida_util_meses: 60, metodo_depreciacion: 'lineal', plan_cuenta_activo: '', plan_cuenta_depreciacion: '', centro_costo: '' });

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);

  const getEstadoBadge = (e) => {
    const map = { activo: 'badge-green', dado_de_baja: 'badge-red', vendido: 'badge-yellow' };
    return map[e] || 'badge-gray';
  };

  if (loading) return <Loading />;

  return (
    <div className="activos-fijos-page">
      <div className="page-header">
        <h1>Activos Fijos</h1>
        <button className="btn-primary" onClick={() => { resetForm(); setEditing(null); setModalOpen(true); }}><FiPlus /> Nuevo Activo</button>
      </div>

      {/* Resumen por categoria */}
      {resumen.length > 0 && (
        <div className="resumen-grid">
          {resumen.map((r, i) => (
            <div key={i} className="stat-card">
              <div className="stat-label">{r.categoria || 'Sin categoria'}</div>
              <div className="stat-value">{r.cantidad} activos</div>
              <div className="stat-sublabel">Valor: {formatCurrency(r.valor_total)} | Dep: {formatCurrency(r.depreciacion_total)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="filtros-container">
        <select className="select-filtro" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="activo">Activo</option>
          <option value="dado_de_baja">Dado de Baja</option>
          <option value="vendido">Vendido</option>
        </select>
        <select className="select-filtro" value={filtroMetodo} onChange={e => setFiltroMetodo(e.target.value)}>
          <option value="">Todos los metodos</option>
          <option value="lineal">Lineal</option>
          <option value="acelerada">Acelerada</option>
        </select>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Codigo</th><th>Nombre</th><th>Categoria</th><th>Metodo</th>
              <th style={{ textAlign: 'right' }}>Valor Adq.</th>
              <th style={{ textAlign: 'right' }}>Dep. Acum.</th>
              <th style={{ textAlign: 'right' }}>Valor Libro</th>
              <th>Estado</th><th style={{ textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {activos.length === 0 ? (
              <tr><td colSpan="10" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Sin activos fijos</td></tr>
            ) : activos.map(a => (
              <React.Fragment key={a.id}>
                <tr className={expandedId === a.id ? 'expanded' : ''}>
                  <td><button className="btn-icon-sm" onClick={() => setExpandedId(expandedId === a.id ? null : a.id)}>{expandedId === a.id ? <FiChevronDown /> : <FiChevronRight />}</button></td>
                  <td className="mono">{a.codigo_activo}</td>
                  <td><strong>{a.nombre}</strong></td>
                  <td>{a.categoria || '-'}</td>
                  <td>{a.metodo_depreciacion}</td>
                  <td className="mono right">{formatCurrency(a.valor_adquisicion)}</td>
                  <td className="mono right" style={{ color: '#dc2626' }}>{formatCurrency(a.depreciacion_acumulada)}</td>
                  <td className="mono right bold">{formatCurrency(a.valor_libro)}</td>
                  <td><span className={`badge ${getEstadoBadge(a.estado)}`}>{a.estado_display || a.estado}</span></td>
                  <td>
                    <div className="acciones-cell">
                      {a.estado === 'activo' && (
                        <>
                          <button className="btn-icon btn-success" title="Depreciar" onClick={() => handleDepreciar(a.id)}><FiTool /></button>
                          <button className="btn-icon btn-warning" title="Dar de baja" onClick={() => { setActivoBaja(a); setMotivoBaja(''); setFechaBaja(''); setBajaModalOpen(true); }}><FiMinusCircle /></button>
                          <button className="btn-icon" title="Editar" onClick={() => handleEdit(a)}><FiEdit2 /></button>
                        </>
                      )}
                      <button className="btn-icon btn-danger" title="Eliminar" onClick={() => handleDelete(a.id)}><FiTrash2 /></button>
                    </div>
                  </td>
                </tr>
                {expandedId === a.id && (
                  <tr className="detalles-row">
                    <td colSpan="10">
                      <div className="detalles-container">
                        <div className="detalle-info-grid">
                          <div><span>Fecha Adquisicion:</span><strong>{a.fecha_adquisicion}</strong></div>
                          <div><span>Vida Util:</span><strong>{a.vida_util_meses} meses</strong></div>
                          <div><span>Valor Residual:</span><strong>{formatCurrency(a.valor_residual)}</strong></div>
                        </div>
                        <h4>Historial de Depreciaciones</h4>
                        {a.depreciaciones?.length > 0 ? (
                          <table className="detalles-table">
                            <thead><tr><th>Fecha</th><th style={{ textAlign: 'right' }}>Monto</th><th>Asiento</th></tr></thead>
                            <tbody>{a.depreciaciones.map((d, i) => (
                              <tr key={i}><td>{d.fecha}</td><td className="mono right">{formatCurrency(d.monto)}</td><td>{d.asiento_id || '-'}</td></tr>
                            ))}</tbody>
                          </table>
                        ) : <p className="text-muted">Sin depreciaciones registradas</p>}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Crear/Editar */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <h2>{editing ? 'Editar Activo Fijo' : 'Nuevo Activo Fijo'}</h2>
            <div className="form-grid">
              <div className="form-group"><label>Nombre *</label><input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Camioneta Toyota 2024" /></div>
              <div className="form-group"><label>Codigo</label><input value={form.codigo_activo} onChange={e => setForm({ ...form, codigo_activo: e.target.value })} placeholder="AF-001" /></div>
              <div className="form-group"><label>Categoria</label><input value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })} placeholder="Vehiculos, Muebles..." /></div>
              <div className="form-group"><label>Fecha Adquisicion *</label><input type="date" value={form.fecha_adquisicion} onChange={e => setForm({ ...form, fecha_adquisicion: e.target.value })} /></div>
              <div className="form-group"><label>Valor Adquisicion *</label><input type="number" value={form.valor_adquisicion} onChange={e => setForm({ ...form, valor_adquisicion: e.target.value })} /></div>
              <div className="form-group"><label>Valor Residual</label><input type="number" value={form.valor_residual} onChange={e => setForm({ ...form, valor_residual: e.target.value })} /></div>
              <div className="form-group"><label>Vida Util (meses)</label><input type="number" value={form.vida_util_meses} onChange={e => setForm({ ...form, vida_util_meses: e.target.value })} /></div>
              <div className="form-group"><label>Metodo Depreciacion</label>
                <select value={form.metodo_depreciacion} onChange={e => setForm({ ...form, metodo_depreciacion: e.target.value })}>
                  <option value="lineal">Lineal</option><option value="acelerada">Acelerada</option>
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleSave}>{editing ? 'Actualizar' : 'Crear'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dar de Baja */}
      {bajaModalOpen && (
        <div className="modal-overlay" onClick={() => setBajaModalOpen(false)}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <h2>Dar de Baja: {activoBaja?.nombre}</h2>
            <div className="form-group"><label>Fecha de Baja</label><input type="date" value={fechaBaja} onChange={e => setFechaBaja(e.target.value)} /></div>
            <div className="form-group" style={{ marginTop: 12 }}><label>Motivo</label><textarea value={motivoBaja} onChange={e => setMotivoBaja(e.target.value)} rows={3} placeholder="Motivo de la baja..." style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }} /></div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setBajaModalOpen(false)}>Cancelar</button>
              <button className="btn-danger-solid" onClick={handleDarDeBaja}>Dar de Baja</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivosFijosPage;
